import { useEffect, useMemo } from "react";
import { ApolloProvider } from "@apollo/client";
import { RouterProvider } from "react-router-dom";
import { createApolloClient } from "@onmo/graphql";
import {
  selectIsAuthenticated,
  selectAuthReady,
  selectIsOnboarded,
  selectSigningIn,
} from "@onmo/auth";
import { routeDefs } from "./routes/Routes";
import { useAppSelector } from "./components/hooks";
import { isQRCodeLoginEnabled, isAndroidApp } from "./utils/platform";
import { LoadingScreen } from "./components/common/LoadingScreen/LoadingScreen";
import { setActivationCode } from "./models/storage";
import { forceLogout } from "./models/auth";
import { initWakeLock } from "./utils/wakeLock";
import { prefetchGuideVideos } from "./models/guideVideos";
import { useSentryUser } from "./hooks/useSentryUser";
import * as Sentry from "@sentry/react";
import { loadIntercom } from "./utils/intercom";

const SentryUser = () => {
  useSentryUser();
  return null;
};

export function App() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isOnboarded = useAppSelector(selectIsOnboarded);
  const authReady = useAppSelector(selectAuthReady);
  const signingIn = useAppSelector(selectSigningIn);
  const apolloClient = useMemo(() => {
    return createApolloClient({
      onGraphQLError: (err, operation) => {
        Sentry.captureException(new Error(`[GraphQL] ${err.message}`), {
          tags: { type: "graphql", operationName: operation.operationName },
          extra: {
            path: err.path,
            code: err.extensions?.code,
          },
        });
      },
      onCrossTenantAuthFailed: forceLogout,
    });
    // Need authReady because we need to get the token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady]);

  useEffect(() => {
    loadIntercom();
    initWakeLock();
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", handler);
    return () => document.removeEventListener("gesturestart", handler);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/activate") {
      const params = new URLSearchParams(window.location.search);
      const codeParams = params.get("code");
      if (codeParams) {
        setActivationCode(codeParams);
      }
    }
  }, []);

  // prefetch guide vides on first open
  useEffect(() => {
    prefetchGuideVideos(apolloClient);
  }, [apolloClient]);

  if (isAndroidApp && signingIn) return <LoadingScreen />;

  // For non-QR code login builds, wait for Keycloak
  if (!isQRCodeLoginEnabled && !authReady) return <LoadingScreen />;

  return (
    <ApolloProvider client={apolloClient}>
      <SentryUser />
      <RouterProvider router={routeDefs(isAuthenticated, isOnboarded)} />
    </ApolloProvider>
  );
}

export default App;
